/**
 * Auth0 SPA configuration loader.
 *
 * Validates VITE_* env vars and shapes them for Auth0Provider.
 * Pure function — accepts a source object so tests don't need to monkeypatch
 * import.meta.env. Production callers use `loadAuth0Config(import.meta.env)`.
 */

export interface Auth0ConfigSource {
  VITE_AUTH0_DOMAIN: string;
  VITE_AUTH0_CLIENT_ID: string;
  VITE_AUTH0_AUDIENCE: string;
  VITE_AUTH0_REDIRECT_URI: string;
}

export interface Auth0Config {
  domain: string;
  clientId: string;
  authorizationParams: {
    audience?: string;
    redirect_uri: string;
    scope: string;
  };
  cacheLocation: 'localstorage' | 'memory';
  useRefreshTokens: boolean;
}

const REQUIRED_VARS: Array<keyof Auth0ConfigSource> = [
  'VITE_AUTH0_DOMAIN',
  'VITE_AUTH0_CLIENT_ID',
  'VITE_AUTH0_REDIRECT_URI',
];

const PLACEHOLDER = '__replace_me__';

/** Runtime callback URL — must match Auth0 Application → Allowed Callback URLs. */
export function resolveRedirectUri(configured: string): string {
  if (typeof window !== 'undefined' && window.location?.origin) {
    return `${window.location.origin}/callback`;
  }
  return configured;
}

export function loadAuth0Config(source: Auth0ConfigSource): Auth0Config {
  for (const key of REQUIRED_VARS) {
    const value = source[key];
    if (!value || !value.trim()) {
      throw new Error(
        `[yhelp-web] Auth0 env var ${key} is missing. Copy .env.example to .env.local and fill it in.`,
      );
    }
    if (value.trim() === PLACEHOLDER) {
      throw new Error(
        `[yhelp-web] Auth0 env var ${key} still holds the "${PLACEHOLDER}" placeholder. Replace with real Auth0 dashboard values.`,
      );
    }
  }

  const audience = source.VITE_AUTH0_AUDIENCE?.trim();
  if (audience?.includes('/api/v2/')) {
    throw new Error(
      '[yhelp-web] VITE_AUTH0_AUDIENCE must not be the Auth0 Management API (/api/v2/). Use your yHelp API identifier or leave empty for login-only.',
    );
  }

  return {
    domain: source.VITE_AUTH0_DOMAIN,
    clientId: source.VITE_AUTH0_CLIENT_ID,
    authorizationParams: {
      ...(audience ? { audience } : {}),
      redirect_uri: resolveRedirectUri(source.VITE_AUTH0_REDIRECT_URI),
      // offline_access enables refresh tokens (silent renew without iframe).
      scope: 'openid profile email offline_access',
    },
    cacheLocation: 'localstorage',
    useRefreshTokens: true,
  };
}
